import { useSyncExternalStore } from 'react';

/**
 * A value shared by every React root on the page.
 *
 * The value lives in the module the store is declared in, not in a component. That is the whole
 * mechanism: a React root only ever sees its own component instances, so state and context cannot
 * cross from one root to another, while a module is evaluated once per URL and everything that
 * imports it gets the same instance.
 *
 * @example
 * ```ts
 * // cartOpenStore.ts - imported by every root that cares
 * export const cartOpenStore = createSharedState(false);
 *
 * // in a component
 * const cartOpen = useSharedState(cartOpenStore);
 * cartOpenStore.set(true);
 * ```
 */
export type SharedState<T> = {
    get: () => T;
    set: (value: T) => void;
    subscribe: (listener: () => void) => () => void;
};

export const createSharedState = <T>(initialValue: T): SharedState<T> => {
    let current = initialValue;
    const listeners = new Set<() => void>();

    return {
        get: () => current,

        set: (value) => {
            if (value === current) {
                return;
            }

            current = value;
            for (const listener of listeners) {
                listener();
            }
        },

        subscribe: (listener) => {
            listeners.add(listener);

            return () => {
                listeners.delete(listener);
            };
        },
    };
};

/** Reads a shared value and re-renders the component whenever it changes. */
export const useSharedState = <T>(store: SharedState<T>): T =>
    useSyncExternalStore(store.subscribe, store.get, store.get);
